import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'
import './index.css'
import { nonceFromHash, runSsoHandOff } from './lib/sso-hand-off'
import { isChunkLoadError, reloadForStaleChunk, startFreshBundleWatch } from './lib/stale-chunk'

// After a deploy, a browser still on the previous bundle requests a JS chunk whose
// hash no longer exists → recover by reloading once. Failures arrive through three
// channels, so listen on all (the ErrorBoundary handles the React.lazy render-throw).
// The reload is time-boxed in reloadForStaleChunk() so a later deploy still self-heals.
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault() // stop Vite re-throwing; we own the recovery
  reloadForStaleChunk()
})
window.addEventListener('error', (e) => {
  if (isChunkLoadError((e as ErrorEvent).error ?? (e as ErrorEvent).message)) reloadForStaleChunk()
})
window.addEventListener('unhandledrejection', (e) => {
  if (isChunkLoadError((e as PromiseRejectionEvent).reason)) reloadForStaleChunk()
})

startFreshBundleWatch()

// Tells the index.html bootstrap watchdog the app actually mounted, so it never does a spurious
// reload. If this never fires (a stale-chunk boot failure), the watchdog recovers with one reload.
function BootSignal() {
  useEffect(() => {
    document.documentElement.dataset.appReady = '1'
    ;(window as unknown as { __navoBooted__?: () => void }).__navoBooted__?.()
  }, [])
  return null
}

// Cross-domain SSO hand-off from the hub (standalone site only: the federated remote never loads this
// file, the hub already holds the session there). A `#sso=` nonce is redeemed BEFORE first render so
// the app boots signed in; the no-nonce first-visit check runs in the background and redirects at most
// once per tab session. Never throws into boot.
async function boot() {
  if (nonceFromHash(window.location.hash)) await runSsoHandOff().catch(() => {})
  else void runSsoHandOff().catch(() => {})
  const el = document.getElementById('root')
  if (el)
    createRoot(el).render(
      <StrictMode>
        <BootSignal />
        <App />
      </StrictMode>
    )
}
void boot()
